import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react"
import { useCallback, useId } from "react"

import { cx } from "../lib/cx"
import styles from "./button.module.css"

/**
 * Кнопка кабинета.
 *
 * Состояния из макета «Оболочка - этап 0», карточка «Кнопки · все состояния»:
 * обычное, наведение, нажато, фокус с клавиатуры, выключено, «Сохраняем…»
 * и отдельно — выключено ПРАВОМ.
 *
 * ПЕРВОЕ РЕШЕНИЕ — deniedReason. Недоступное действие НЕ ПРЯЧЕТСЯ: прячут то, чего
 * у человека не будет никогда, а выключенным с пояснением показывают то, что он может
 * получить, попросив владельца. Администратор, у которого кнопка «Изменить прошедшую
 * запись» просто исчезла, решит, что система сломалась, и позвонит в поддержку.
 * Поэтому причина — не необязательная подсказка, а сам способ выключения: передав её,
 * разработчик получает и выключение, и объяснение, и не может выключить кнопку молча.
 *
 * ВТОРОЕ РЕШЕНИЕ — loadingLabel. «Сохраняем…» меняет ПОДПИСЬ, но не размер и не место
 * кнопки: кнопка, которая на время запроса превращается в один спиннер, уезжает
 * из-под пальца, и следующий щелчок попадает в соседнюю.
 */

export type ButtonVariant = "primary" | "secondary" | "danger" | "danger-ghost" | "ghost"
export type ButtonSize = "md" | "sm" | "touch"

const VARIANT_STYLE: Readonly<Record<ButtonVariant, string | undefined>> = {
  primary: styles.primary,
  secondary: styles.secondary,
  danger: styles.danger,
  "danger-ghost": styles.dangerGhost,
  ghost: styles.ghost,
}

const SIZE_STYLE: Readonly<Record<ButtonSize, string | undefined>> = {
  md: undefined,
  sm: styles.sizeSm,
  touch: styles.sizeTouch,
}

type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "type">

export interface ButtonProps extends NativeButtonProps {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  /** Растянуть на всю ширину: главная кнопка на телефоне мастера именно такая. */
  readonly block?: boolean
  /** Идёт запрос: кнопка на месте, подпись сменилась, повторное нажатие невозможно. */
  readonly loading?: boolean
  /** Чем подписать кнопку, пока идёт запрос. По умолчанию подпись не меняется. */
  readonly loadingLabel?: string | undefined
  /**
   * Действие есть, но у этого человека нет права. Строка — ПОЯСНЕНИЕ, кто может.
   * Кнопка остаётся видимой и доступной с клавиатуры (см. комментарий ниже).
   */
  readonly deniedReason?: string | undefined
  readonly type?: "button" | "submit"
  readonly className?: string | undefined
  readonly children?: ReactNode
}

export function Button(props: ButtonProps): ReactNode {
  const {
    variant = "secondary",
    size = "md",
    block = false,
    loading = false,
    loadingLabel,
    deniedReason,
    type = "button",
    className,
    children,
    onClick,
    disabled,
    ...rest
  } = props

  const noteId = useId()
  const denied = typeof deniedReason === "string" && deniedReason.length > 0
  // Занятая кнопка и кнопка без права не должны срабатывать, но по разным причинам,
  // поэтому и признак в разметке разный: aria-busy против aria-disabled.
  const blocked = loading || denied

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (blocked) {
        // aria-disabled браузер не соблюдает — нажатие придётся отменить самим.
        event.preventDefault()
        return
      }
      onClick?.(event)
    },
    [blocked, onClick],
  )

  return (
    <>
      <button
        {...rest}
        type={type}
        className={cx(
          styles.button,
          VARIANT_STYLE[variant],
          SIZE_STYLE[size],
          block ? styles.block : undefined,
          className,
        )}
        onClick={handleClick}
        disabled={disabled}
        aria-busy={loading ? true : undefined}
        /*
         * ВЫКЛЮЧЕНО ПРАВОМ — ЭТО aria-disabled, А НЕ disabled.
         * Настоящий disabled убирает кнопку из обхода клавишей Tab, и до пояснения
         * «Изменять прошедшие записи может только управляющий» уже не добраться:
         * ни фокусом, ни чтением с экрана, ни всплывающей подсказкой браузера —
         * над выключенной кнопкой она не показывается. То есть обещание макета
         * «выключено правом — с пояснением, не спрятано» выполняется только наполовину.
         * Здесь кнопка остаётся живой, а нажатие гасится обработчиком выше.
         */
        aria-disabled={denied ? true : undefined}
        title={denied ? deniedReason : rest.title}
        aria-describedby={denied ? noteId : rest["aria-describedby"]}
      >
        {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
        {loading && loadingLabel !== undefined ? loadingLabel : children}
      </button>
      {denied ? (
        <span id={noteId} className="plnr-visually-hidden">
          {deniedReason}
        </span>
      ) : null}
    </>
  )
}

export interface IconButtonProps extends Omit<ButtonProps, "children" | "variant" | "size" | "block"> {
  /**
   * Имя действия словами. Обязательное и без значения по умолчанию: у кнопки-значка нет
   * подписи, и без имени она звучит как «кнопка» — колокольчик, шестерёнка и крестик
   * становятся неотличимы для того, кто слушает экран.
   */
  readonly label: string
  /** Значок рисует вызывающий код; библиотека не заводит собственного набора. */
  readonly icon: ReactNode
  /** Обвести рамкой — как колокольчик в шапке макета (32×32) и на телефоне (40×40). */
  readonly framed?: boolean
  /** Размер lg (40×40) — для телефона мастера: в 32 пикселя стойки он не попадает. */
  readonly size?: "md" | "lg"
  /**
   * Счётчик в правом верхнем углу: колокольчик с числом непрочитанного.
   * ВНИМАНИЕ: aria-label заменяет собой ВСЁ содержимое кнопки, поэтому число
   * из счётчика голосом не прозвучит — его надо повторить в label
   * («Уведомления, 3 непрочитанных»). Иначе счётчик виден, но не слышен.
   */
  readonly badge?: ReactNode
}

export function IconButton(props: IconButtonProps): ReactNode {
  const { label, icon, framed = false, size = "md", badge, className, ...rest } = props
  return (
    <Button
      {...rest}
      variant="ghost"
      className={cx(
        styles.icon,
        framed ? styles.iconFramed : undefined,
        size === "lg" ? styles.iconLarge : undefined,
        className,
      )}
      aria-label={label}
      title={rest.deniedReason ?? label}
    >
      <span className={styles.glyph} aria-hidden="true">
        {icon}
      </span>
      {badge}
    </Button>
  )
}
